import type { CacheConfig, GraphQLResponse } from 'relay-runtime';

import {
	BadStatusError,
	isSessionExpiredError,
	NoNetworkError,
	causedByAbortError,
} from '@confluence/network';
import {
	getNetworkStatus,
	setNetworkStatus,
	ClientNetworkStatus,
} from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache.ts';

export const updateNetworkStatusUnlessSessionExpired = (newStatus: ClientNetworkStatus): void => {
	const currentStatus = getNetworkStatus();
	if (newStatus === currentStatus || currentStatus === ClientNetworkStatus.SESSION_EXPIRED) {
		return;
	}
	setNetworkStatus(newStatus);
};

export const isIpBlockedError = (error: Error): error is BadStatusError => {
	return error instanceof BadStatusError && error.response.status === 403;
};

export const isRateLimitedError = (error: Error): error is BadStatusError => {
	return error instanceof BadStatusError && error.response.status === 429;
};

export const shouldTreatAsOfflineError = (error: Error): error is NoNetworkError => {
	return error instanceof NoNetworkError && !error.ignore && !causedByAbortError(error);
};

/**
 * Whether this request opted out of offline handling, i.e. Apollo's `ignoreNoNetworkError`.
 *
 * On Apollo the opt-out rides the fetch init: callers pass
 * `context: { fetchOptions: { ignoreNoNetworkError: true } }`, Apollo's HTTP link merges it into the
 * request, `cfetch` stamps `error.ignore` on the resulting `NoNetworkError`, and
 * `shouldTreatAsOfflineError` then returns false — so the offline handler is skipped entirely: no
 * OFFLINE status, and the error propagates whatever the feature gate says.
 *
 * Nothing on the Relay transport forwards `ignoreNoNetworkError` into the outgoing fetch init, so
 * `error.ignore` is never set and the opt-out was silently dropped. This restores it by reading the
 * same flag from `cacheConfig.metadata`, where the interop layer already forwards Apollo `context`
 * verbatim, so existing Apollo-shaped call sites work untouched. `suppressNetworkStatusUpdate` is
 * accepted as a transport-native alias.
 *
 * Reading it from `cacheConfig` rather than plumbing it through the fetch init matters under
 * batching: `getNetwork`'s fetch function runs once per Relay operation, so each operation carries
 * its own `cacheConfig` down its own middleware chain even when several share one HTTP request. That
 * makes this per-operation correct by construction and — unlike `metadata.headers` — needs no batch
 * bypass, so opting out costs nothing in request count.
 */
export const isNoNetworkErrorIgnored = (cacheConfig: CacheConfig): boolean => {
	const metadata = cacheConfig.metadata;
	if (metadata == null) {
		return false;
	}

	if (metadata.suppressNetworkStatusUpdate === true) {
		return true;
	}

	const fetchOptions = metadata.fetchOptions;
	return (
		fetchOptions != null &&
		typeof fetchOptions === 'object' &&
		(fetchOptions as Record<string, unknown>).ignoreNoNetworkError === true
	);
};

export const hasSessionExpiredErrors = (result: GraphQLResponse): boolean => {
	const errors = 'errors' in result ? result.errors : undefined;
	if (!errors || errors.length === 0) {
		return false;
	}
	return errors.some((error) => {
		return isSessionExpiredError(error);
	});
};
